// Central catalogue of every tool's help text. Each entry powers a "?" tooltip
// (see HelpTooltip). `what` = what the tool does, `how` = how to use it.

export interface ToolHelp {
  label: string;
  what: string;
  how: string;
}

export const toolHelp = {
  // --- Library ---
  import: {
    label: 'Import',
    what: 'Brings photos from your computer into the library and uploads them to your private cloud storage.',
    how: 'Click Import (or drag files onto the grid) and pick one or more images. A thumbnail is generated automatically.',
  },
  rating: {
    label: 'Rating',
    what: 'Marks how much you like a photo using 0–5 stars.',
    how: 'Click a star on a photo, or press 1–5 on the keyboard. Press 0 to clear.',
  },
  flag: {
    label: 'Flagging',
    what: 'Quickly marks a photo as a Pick (keep) or Reject (discard candidate).',
    how: 'Click the flag / reject icon, or press P to pick and X to reject.',
  },
  colorLabel: {
    label: 'Color Label',
    what: 'Assigns a color tag for fast visual grouping (e.g. red = to edit, green = done).',
    how: 'Click a color swatch on the photo or in the info panel. Click the same color again to remove it.',
  },
  keywords: {
    label: 'Keywords & Metadata',
    what: 'Adds searchable tags and camera information to a photo.',
    how: 'Type keywords separated by commas in the Keywords box and press Enter. Filter the library by any keyword.',
  },
  collections: {
    label: 'Collections',
    what: 'Virtual albums that group photos without moving or duplicating the files.',
    how: 'Create a collection in the left panel, then add the selected photo to it. A photo can live in many collections.',
  },

  // --- Crop / geometry ---
  crop: {
    label: 'Crop & Rotate',
    what: 'Changes the framing and straightens tilted horizons.',
    how: 'Drag the crop handles to reframe, drag inside to reposition, and use the Straighten slider to level the horizon.',
  },

  // --- White balance & basic tone ---
  whiteBalance: {
    label: 'White Balance',
    what: 'Corrects unnatural color casts by adjusting color temperature and tint.',
    how: 'Move Temp toward blue/yellow to cool or warm the image, and Tint toward green/magenta to remove casts.',
  },
  temp: {
    label: 'Temperature',
    what: 'Shifts the image cooler (blue) or warmer (yellow).',
    how: 'Drag left for a cooler look, right for a warmer look.',
  },
  tint: {
    label: 'Tint',
    what: 'Balances green versus magenta casts, often from artificial light.',
    how: 'Drag left to add green, right to add magenta until skin tones look natural.',
  },
  exposure: {
    label: 'Exposure',
    what: 'Brightens or darkens the whole image (overall lightness).',
    how: 'Drag right to brighten, left to darken. Watch the histogram to avoid clipping.',
  },
  contrast: {
    label: 'Contrast',
    what: 'Increases or decreases the difference between light and dark areas.',
    how: 'Drag right for punchier images, left for a flatter, softer look.',
  },
  highlights: {
    label: 'Highlights',
    what: 'Recovers or emphasises detail in the brighter parts of the image.',
    how: 'Drag left to bring back detail in blown skies, right to brighten highlights.',
  },
  shadows: {
    label: 'Shadows',
    what: 'Recovers or deepens detail in the darker parts of the image.',
    how: 'Drag right to open up shadow detail, left to deepen shadows.',
  },
  whites: {
    label: 'Whites',
    what: 'Sets the brightest point of the image (the white clipping point).',
    how: 'Drag right to make the brightest tones brighter; hold Alt while dragging to see clipping.',
  },
  blacks: {
    label: 'Blacks',
    what: 'Sets the darkest point of the image (the black clipping point).',
    how: 'Drag left to deepen the darkest tones for richer blacks; hold Alt to see clipping.',
  },

  // --- Color ---
  vibrance: {
    label: 'Vibrance',
    what: 'Boosts the intensity of the more muted colors while protecting skin tones.',
    how: 'Drag right for richer color that stays natural, left to mute colors.',
  },
  saturation: {
    label: 'Saturation',
    what: 'Increases or decreases the intensity of all colors equally.',
    how: 'Drag right for more vivid color, left toward black & white.',
  },
  hsl: {
    label: 'HSL / Color Mixer',
    what: 'Adjusts the Hue, Saturation and Luminance of individual color ranges.',
    how: 'Pick a color channel, then change its hue (shade), saturation (intensity) and luminance (brightness).',
  },
  colorGrading: {
    label: 'Color Grading',
    what: 'Adds color tints separately to the shadows, midtones and highlights for a cinematic look.',
    how: 'Drag inside each wheel to choose a tint and strength; use Balance to favour shadows or highlights.',
  },

  // --- Detail ---
  sharpening: {
    label: 'Sharpening',
    what: 'Enhances edge definition to make the image look crisper.',
    how: 'Increase Amount for more bite; keep Radius small for fine detail. Zoom to 100% to judge.',
  },
  noiseReduction: {
    label: 'Noise Reduction',
    what: 'Smooths out the speckled grain common in high-ISO / low-light photos.',
    how: 'Raise Luminance to reduce brightness grain and Color to remove colored speckles. Avoid over-smoothing.',
  },
  texture: {
    label: 'Texture',
    what: 'Emphasises or softens medium-sized detail like skin, bark or fabric.',
    how: 'Drag right to enhance fine texture, left to smooth it (e.g. skin) without affecting overall contrast.',
  },
  clarity: {
    label: 'Clarity',
    what: 'Adds midtone contrast for a punchier, sharper-looking image.',
    how: 'Drag right for a bold, gritty look, left for a soft, dreamy feel.',
  },
  dehaze: {
    label: 'Dehaze',
    what: 'Cuts through atmospheric haze/fog to reveal contrast and color — or adds haze.',
    how: 'Drag right to clear haze and boost distant detail, left to add a soft, foggy atmosphere.',
  },

  // --- Local adjustments ---
  brush: {
    label: 'Brush Tool',
    what: 'Paints adjustments onto specific areas by hand.',
    how: 'Set brush size and feather, paint over an area, then adjust the sliders to affect only what you painted.',
  },
  linearGradient: {
    label: 'Linear Gradient',
    what: 'Applies an adjustment that fades gradually across the image (e.g. darkening a sky).',
    how: 'Drag across the image to set direction and length; the effect fades between the two lines.',
  },
  radialGradient: {
    label: 'Radial Gradient',
    what: 'Applies an adjustment inside or outside an ellipse (e.g. a spotlight on a subject).',
    how: 'Drag to draw an ellipse, adjust feather, then edit the sliders. Invert to affect the outside instead.',
  },
  masking: {
    label: 'Masking',
    what: 'Selects subjects, skies, backgrounds or objects so you can edit them independently.',
    how: 'Choose a mask type, refine the selection, then apply local adjustments. (Automatic AI selections coming soon.)',
  },

  // --- Repair ---
  heal: {
    label: 'Healing / Remove',
    what: 'Removes dust spots, blemishes and small unwanted objects by blending in nearby texture.',
    how: 'Click or paint over the spot; the tool samples a clean area and blends it to hide the mark.',
  },
  clone: {
    label: 'Clone Tool',
    what: 'Copies pixels exactly from one area to another.',
    how: 'Set a source point, then paint over the target area to duplicate the sampled pixels.',
  },

  // --- Lens & geometry ---
  lensCorrections: {
    label: 'Lens Corrections',
    what: 'Fixes lens distortion (bulging/pinching) and darkened corners (vignetting).',
    how: 'Adjust Distortion to straighten bowed lines and Vignette to brighten or darken the corners.',
  },
  transform: {
    label: 'Transform / Geometry',
    what: 'Corrects perspective problems such as leaning buildings or converging verticals.',
    how: 'Use the Vertical and Horizontal sliders to straighten keystoning until lines look upright and level.',
  },
} as const satisfies Record<string, ToolHelp>;

export type ToolHelpId = keyof typeof toolHelp;
